import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";

function Item({ to, children, onClick }) {
  return (
    <NavLink
      to={to}
      end
      onClick={onClick}
      className={({ isActive }) =>
        [
          "block rounded-lg px-3 py-2 text-sm transition",
          isActive
            ? "bg-white/10 text-white"
            : "text-white/80 hover:bg-white/10 hover:text-white",
        ].join(" ")
      }
    >
      {children}
    </NavLink>
  );
}

export default function AdminLayout({ children }) {
  const loc = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Close sidebar when route changes on mobile
  useEffect(() => {
    setSidebarOpen(false);
  }, [loc.pathname]);

  // Lock body scroll when sidebar is open on mobile
  useEffect(() => {
    document.body.style.overflow = sidebarOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [sidebarOpen]);

  const close = () => setSidebarOpen(false);

  const navItems = (
    <div className="flex flex-col gap-1">
      <Item to="/admin/users"        onClick={close}>Users</Item>
      <Item to="/admin/tutors"       onClick={close}>Tutors</Item>
      <Item to="/admin/credentials"  onClick={close}>Credentials</Item>
      <Item to="/admin/requests"     onClick={close}>Requests</Item>
      <Item to="/admin/bookings"     onClick={close}>Bookings</Item>
      <Item to="/admin/disputes"          onClick={close}>Disputes</Item>
      <Item to="/admin/request-disputes" onClick={close}>Refund Disputes</Item>
      <Item to="/admin/wallets"      onClick={close}>Wallets</Item>
      <Item to="/admin/transactions" onClick={close}>Transactions</Item>
      <Item to="/admin/contacts"     onClick={close}>Contacts</Item>
      <Item to="/admin/logs"         onClick={close}>Logs</Item>
      <Item to="/admin/audit"        onClick={close}>Audit</Item>
    </div>
  );

  return (
    <div className="mx-auto max-w-6xl px-4 md:px-6 py-10 text-white">
      {/* Header row */}
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="font-unbounded text-3xl font-bold">Admin</h1>
          <p className="mt-2 text-white/70">Internal tools (private).</p>
        </div>

        {/* Mobile sidebar toggle */}
        <button
          onClick={() => setSidebarOpen((p) => !p)}
          className="md:hidden flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-sm text-white/80 hover:bg-white/10 transition"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="7"  x2="21" y2="7"  />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="17" x2="21" y2="17" />
          </svg>
          Menu
        </button>
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={close}
        />
      )}

      {/* Mobile sidebar drawer */}
      <div
        className={[
          "fixed top-0 left-0 z-50 h-full w-64 bg-[#272c39] shadow-2xl transition-transform duration-300 md:hidden flex flex-col",
          sidebarOpen ? "translate-x-0" : "-translate-x-full",
        ].join(" ")}
      >
        <div className="flex items-center justify-between border-b border-white/10 px-4 py-4">
          <span className="font-unbounded text-lg font-bold">Admin</span>
          <button onClick={close} className="rounded-lg p-2 text-white/70 hover:bg-white/10 transition">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6"  y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          <div className="px-3 py-2 text-xs text-white/50">Navigation</div>
          {navItems}
        </div>
        <div className="border-t border-white/10 p-3">
          <Link to="/dashboard" onClick={close} className="block rounded-lg px-3 py-2 text-sm text-white/70 hover:text-white hover:bg-white/10 transition">
            ← Back to dashboard
          </Link>
        </div>
      </div>

      {/* Desktop layout */}
      <div className="grid gap-4 md:grid-cols-[220px_1fr]">
        {/* Desktop sidebar */}
        <aside className="hidden md:block rounded-2xl bg-white/5 ring-1 ring-white/10 p-2 h-fit">
          <div className="px-3 py-2 text-xs text-white/50">Navigation</div>
          {navItems}
          <div className="mt-3 border-t border-white/10 pt-3 px-3 pb-2">
            <Link to="/dashboard" className="text-sm text-white/70 hover:text-white transition">
              ← Back to dashboard
            </Link>
          </div>
        </aside>

        <main className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4 min-w-0">
          {children}
        </main>
      </div>
    </div>
  );
}
